import type { IGenericPaymentItem } from '../components/GenericPaymentModal';
import type { ICurrencyInfo } from '../redux/checkout/types';
import type { ISaleObject } from '../types/global/helper';
import { showToastMessage } from './AppUtils';
import {
    additionalFeesFromPreview,
    type SandboxCoursePaymentPayload,
    type SandboxFeePreview,
} from './openSandboxCourseCheckout';

export type SandboxPriceConversion = {
    original_currency?: string;
    transaction_currency?: string;
    rate?: number;
    converted_amount?: number;
    formatted_for_display?: string | null;
    defaulted_to_usd?: boolean;
    fee_preview?: SandboxFeePreview;
    processor_fee_profile?: string;
    student_total?: number;
};

export type SandboxOfficeHoursQuote = {
    config_id: string;
    start_time: string;
    end_time?: string;
    amount: number;
    currency?: string;
    teacher_id?: string;
    notes?: string;
    project_id?: string;
    project_title?: string;
    timezone?: string;
    booker_name?: string;
    booker_email?: string;
};

/**
 * Builds payment payload for office hours.
 * Prefers a server quote (no booking yet). Legacy bookingId path still supported.
 */
export function buildSandboxOfficeHoursPayment(params: {
    /** @deprecated Prefer quote — booking is created after payment. */
    bookingId?: string;
    quote?: SandboxOfficeHoursQuote | null;
    title: string;
    amountUsd?: number;
    currency?: string;
    startLabel?: string;
    conversion?: SandboxPriceConversion | null;
}): SandboxCoursePaymentPayload | null {
    const amountUsd = Number(params.quote?.amount ?? params.amountUsd) || 0;
    const priceCents = Math.round(amountUsd * 100);
    if (priceCents <= 0) {
        showToastMessage('This slot does not require payment', 'info');
        return null;
    }

    const original = (
        params.conversion?.original_currency ||
        params.quote?.currency ||
        params.currency ||
        'usd'
    ).toLowerCase();
    const transaction = (params.conversion?.transaction_currency || params.currency || original).toLowerCase();
    const rate = Number(params.conversion?.rate) || 1;
    const useLocal =
        Boolean(params.conversion?.formatted_for_display) &&
        transaction !== original &&
        rate > 0 &&
        !params.conversion?.defaulted_to_usd;

    const chargeCents = useLocal
        ? Math.round((Number(params.conversion?.converted_amount) || amountUsd * rate) * 100)
        : priceCents;
    const chargeCurrency = useLocal ? transaction : original;
    const additionalFees = additionalFeesFromPreview(params.conversion?.fee_preview);
    const processorFeeProfile =
        params.conversion?.processor_fee_profile || params.conversion?.fee_preview?.processor_fee_profile;

    const quote = params.quote;
    const itemId = params.bookingId || (quote ? `oh-${quote.config_id}-${quote.start_time}` : `oh-${Date.now()}`);

    const items: IGenericPaymentItem[] = [
        {
            id: itemId,
            title: params.title || 'Office hours',
            description: params.startLabel || '1:1 Meet slot with your instructor',
            original_price: chargeCents,
            price: chargeCents,
            currency: chargeCurrency,
            quantity: 1,
        },
    ];

    const saleObject: ISaleObject = {
        category: 'sandbox_office_hours',
        item: items,
        transaction_currency: chargeCurrency,
        // Only link a Booking when one already exists (legacy pending_payment).
        ...(params.bookingId
            ? {
                  linked_event: params.bookingId,
                  linked_event_type: 'Booking' as const,
              }
            : {}),
        max_items: 1,
        ...({
            metadata: {
                customer_pays_service_charge: true,
                override_tax_percent: 0,
                catalog_amount_usd: amountUsd,
                catalog_currency: 'usd',
                ...(processorFeeProfile ? { processor_fee_profile: processorFeeProfile } : {}),
                ...(params.bookingId ? { booking_id: params.bookingId } : {}),
                ...(quote
                    ? {
                          config_id: quote.config_id,
                          start_time: quote.start_time,
                          end_time: quote.end_time,
                          teacher_id: quote.teacher_id,
                          notes: quote.notes,
                          project_id: quote.project_id,
                          project_title: quote.project_title,
                          timezone: quote.timezone,
                          booker_name: quote.booker_name,
                          booker_email: quote.booker_email,
                      }
                    : {}),
            },
        } as any),
    };

    const currencyInfo: ICurrencyInfo = {
        transaction_currency: chargeCurrency,
        original_currency: original,
        rate: useLocal ? rate : 1,
    };

    return { items, saleObject, currencyInfo, additionalFees };
}
